'use client'

import { Button, Link, Sidebar, SidebarProps } from '@gyym/ui'
import { ErrorOutline, Instagram, Line, TwitterX } from '@gyym/ui/icons'
import React from 'react'

import { CorpLpBanner } from '@/components/UI/CorpLpBanner'
import {
  useAuthUser,
  useCheckResendVerificationEmail,
  useLogout,
} from '@/modules/Auth'
import { passCircleImages, useAuthUserGradeStatus } from '@/modules/Grades'
import { useAuthUserSubscription } from '@/modules/Subscriptions'

import {
  Badge,
  LabelInner,
  ListItemLink,
  StyledFacilityLpBanner,
  UserGrade,
} from './styles'

const { Section, List, ListItem, Heading, CircleIconButton } = Sidebar

export type SidebarContainerProps = SidebarProps

export const SidebarContainer = ({
  ...sidebarProps
}: SidebarContainerProps) => {
  const { isLoggedIn, authUser, isVerified, isCorporateUser } = useAuthUser()
  const {
    currentGrade,
    lowerGrade,
    upperGrade,
    isForecastUp,
    isForecastKeep,
    isForecastDown,
    usageNumUntilNextGrade,
    usageNumToKeepGrade,
  } = useAuthUserGradeStatus() ?? {}

  const subscription = useAuthUserSubscription()
  const lastPaymentFailure = subscription?.lastPaymentFailure

  // Sidebar は閉じている間も中身をマウントしたままにするため、
  // 一度開くまでは法人会員サービスLPのバナーを描画せず画像を読み込ませない
  const [hasOpened, setHasOpened] = React.useState(sidebarProps.open)
  if (sidebarProps.open && !hasOpened) setHasOpened(true)

  const { shouldResendVerificationEmail, openResendVerificationDialog } =
    useCheckResendVerificationEmail()

  const checkEmailVerified = React.useCallback(
    (e: React.MouseEvent) => {
      if (!shouldResendVerificationEmail) return
      e.preventDefault()
      openResendVerificationDialog()
    },
    [openResendVerificationDialog, shouldResendVerificationEmail],
  )

  const { logout, isLoggingOut } = useLogout()

  const onLogout = async () => {
    await logout()
    sidebarProps.onClose()
  }

  const aboutMenus = (
    <ListItem mt={6} fullWidth onClick={sidebarProps.onClose}>
      <Heading>GYYMをもっと知る</Heading>

      <List mt={2}>
        <ListItem fullWidth onClick={sidebarProps.onClose}>
          <ListItemLink
            href="https://service.gyym.jp/teiki/about"
            target="_blank"
          >
            GYYMについて
          </ListItemLink>
        </ListItem>

        <ListItem fullWidth onClick={sidebarProps.onClose}>
          <ListItemLink href="/pricing">ご利用料金</ListItemLink>
        </ListItem>

        <ListItem fullWidth onClick={sidebarProps.onClose}>
          <ListItemLink href="/pass">GYYM PASSについて</ListItemLink>
        </ListItem>

        <ListItem fullWidth onClick={sidebarProps.onClose}>
          <ListItemLink href="/media/" target="_blank" external>
            GYYM WELLNESS JOURNAL
          </ListItemLink>
        </ListItem>
      </List>
    </ListItem>
  )

  return (
    <Sidebar {...sidebarProps}>
      <Section>
        <List justifyContent="flex-end">
          {isLoggedIn ? (
            <ListItem>
              <Button
                variant="inverted"
                onClick={onLogout}
                disabled={isLoggingOut}
              >
                ログアウト
              </Button>
            </ListItem>
          ) : (
            <>
              <ListItem>
                <Button
                  variant="inverted"
                  onClick={sidebarProps.onClose}
                  asChild
                >
                  <Link href="/login">ログイン</Link>
                </Button>
              </ListItem>
              <ListItem ml={2}>
                <Button
                  variant="inverted"
                  onClick={sidebarProps.onClose}
                  asChild
                >
                  <Link href="/signup">新規登録</Link>
                </Button>
              </ListItem>
            </>
          )}
        </List>
      </Section>

      {/* 法人会員は GYYM PASS プログラム対象外のため非表示 */}
      {isLoggedIn && currentGrade && !isCorporateUser && (
        <Section>
          <UserGrade.Container>
            <div>
              <UserGrade.Img
                src={passCircleImages[currentGrade.key]}
                alt={currentGrade.name}
              />
              <UserGrade.Label>{currentGrade.name}</UserGrade.Label>
            </div>
            <UserGrade.Content>
              <UserGrade.ContentInner>
                {upperGrade ? (
                  // 上位PASSがある場合
                  <>
                    {(isForecastKeep || isForecastDown) && (
                      // クエスト未達成
                      <>
                        <UserGrade.Text>{upperGrade?.name}まで</UserGrade.Text>
                        <UserGrade.Text size="lg">
                          あとご利用{usageNumUntilNextGrade}回
                        </UserGrade.Text>
                      </>
                    )}

                    {isForecastUp && (
                      // クエスト達成
                      <>
                        <UserGrade.Text size="lg">
                          今月のクエスト達成 📣
                        </UserGrade.Text>
                        <UserGrade.Text mt={2}>
                          来月のランクアップが楽しみですね！
                        </UserGrade.Text>
                      </>
                    )}
                  </>
                ) : (
                  // 最上位PASSの場合
                  <UserGrade.Text mt={1}>
                    おめでとうございます！
                    <br />
                    あなたは{currentGrade?.name}です！
                  </UserGrade.Text>
                )}

                {isForecastKeep && upperGrade && (
                  // PASSの維持が確定していて、上位PASSがある場合
                  <UserGrade.Text type="positive">
                    今月中にあと{usageNumUntilNextGrade}回のご利用で
                    <br />
                    {upperGrade?.name}にランクアップ
                  </UserGrade.Text>
                )}

                {isForecastDown && !authUser?.isExemptedFromDowngrade && (
                  // ダウングレードしそうな場合で、ダウングレードが免除されていない場合
                  <UserGrade.Text type="negative">
                    今月中にあと{usageNumToKeepGrade}回ご利用がない場合
                    <br />
                    {lowerGrade?.name}にランクダウン
                  </UserGrade.Text>
                )}
              </UserGrade.ContentInner>
            </UserGrade.Content>
          </UserGrade.Container>
        </Section>
      )}

      <Section>
        <List>
          <ListItem fullWidth onClick={sidebarProps.onClose}>
            <ListItemLink href="/">ホーム</ListItemLink>
          </ListItem>

          <ListItem fullWidth onClick={sidebarProps.onClose}>
            <ListItemLink href="/notifications">お知らせ</ListItemLink>
          </ListItem>

          {!isLoggedIn && aboutMenus}

          {authUser && isLoggedIn && (
            <ListItem mt={6} fullWidth onClick={sidebarProps.onClose}>
              <Heading>My Menu</Heading>

              <List mt={2}>
                <ListItem fullWidth onClick={sidebarProps.onClose}>
                  <ListItemLink href="/me" onClick={checkEmailVerified}>
                    マイページ
                  </ListItemLink>
                </ListItem>

                <ListItem fullWidth onClick={sidebarProps.onClose}>
                  <ListItemLink
                    href="/me/favorites"
                    onClick={checkEmailVerified}
                  >
                    お気に入り
                  </ListItemLink>
                </ListItem>

                <ListItem fullWidth onClick={sidebarProps.onClose}>
                  <ListItemLink
                    href="/me/reservations"
                    onClick={checkEmailVerified}
                  >
                    予約一覧
                  </ListItemLink>
                </ListItem>

                <ListItem fullWidth onClick={sidebarProps.onClose}>
                  <ListItemLink href="/me/points" onClick={checkEmailVerified}>
                    <LabelInner>
                      ポイント情報・購入
                      {lastPaymentFailure && <ErrorOutline />}
                    </LabelInner>
                    <Badge>{authUser.point}P</Badge>
                  </ListItemLink>
                </ListItem>

                {isVerified && (
                  <ListItem fullWidth onClick={sidebarProps.onClose}>
                    <ListItemLink href="/me/referral">友達招待</ListItemLink>
                  </ListItem>
                )}
              </List>
            </ListItem>
          )}

          <ListItem mt={6}>
            <Heading>Search</Heading>

            <List mt={2}>
              <ListItem fullWidth onClick={sidebarProps.onClose}>
                <ListItemLink href="/facilities?sortBy=distance&order=asc">
                  現在地から探す
                </ListItemLink>
              </ListItem>

              <ListItem fullWidth onClick={sidebarProps.onClose}>
                <ListItemLink href="/search?type=area">
                  エリアから探す
                </ListItemLink>
              </ListItem>

              <ListItem fullWidth onClick={sidebarProps.onClose}>
                <ListItemLink href="/search?type=name">
                  施設名から探す
                </ListItemLink>
              </ListItem>

              <ListItem fullWidth onClick={sidebarProps.onClose}>
                <ListItemLink href="/search?type=condition">
                  条件から探す
                </ListItemLink>
              </ListItem>
            </List>
          </ListItem>

          {isLoggedIn && aboutMenus}
        </List>
      </Section>

      <Section divider>
        <List>
          <ListItem fullWidth onClick={sidebarProps.onClose}>
            <ListItemLink href="https://support.gyym.jp/" target="_blank">
              FAQ・お問い合わせ
            </ListItemLink>
          </ListItem>
        </List>
      </Section>

      <Section mt={10}>
        <Heading>Follow</Heading>

        <List mt={4}>
          <ListItem onClick={sidebarProps.onClose}>
            <CircleIconButton
              target="_blank"
              href="https://twitter.com/gyymofficial"
              aria-label="公式X(Twitter)を開く"
            >
              <TwitterX />
            </CircleIconButton>
          </ListItem>
          <ListItem onClick={sidebarProps.onClose} ml={3}>
            <CircleIconButton
              target="_blank"
              href="https://www.instagram.com/gyym_official"
              aria-label="公式Instagramを開く"
            >
              <Instagram />
            </CircleIconButton>
          </ListItem>
          <ListItem onClick={sidebarProps.onClose} ml={3}>
            <CircleIconButton
              target="_blank"
              href="https://aura-mico.jp/qr-codes/1011c8082b8e5dab0c7a735e67a10805/preview"
              aria-label="公式LINEを開く"
            >
              <Line />
            </CircleIconButton>
          </ListItem>
        </List>
      </Section>

      <Section mt={10}>
        <StyledFacilityLpBanner utmCampaign="side_menu" />
      </Section>

      {hasOpened && (
        <Section mt={10}>
          {/* タブレット幅ではメニューが広がるため、施設バナー同様に幅を抑えて中央寄せにする */}
          <CorpLpBanner
            placement="side_menu"
            size="sp"
            className="mx-auto max-w-[360px]"
            onClick={sidebarProps.onClose}
          />
        </Section>
      )}
    </Sidebar>
  )
}
